/**
 * services/api/products.js
 *
 * APIs por módulo do produto. Cada export mantém a assinatura externa que os
 * callers já usam (getByProduct, create, update, delete) — internamente:
 *
 * - productsAPI / plansAPI / checkoutsAPI / couponsAPI / productAffiliatesAPI
 *   foram migrados pra V2 (productsV2API, plansV2API, checkoutsV2API,
 *   couponsV2API, affiliatesV2API). Shape de resposta envelopado como
 *   { data: ... } pra UI legada.
 *
 * - productComponentsAPI / upsellsAPI / productUrlsAPI /
 *   affiliateCommissionsAPI / affiliateGoalsAPI / productFilesAPI continuam V1
 *   (regra de negócio inalterada na V2; tabelas existem com schema intacto).
 */

import axios from 'axios';
import { API_URL } from '../../config/api';
import { productsV2API } from './v2/products';
import { plansV2API } from './v2/plans';
import { checkoutsV2API } from './v2/checkouts';
import { couponsV2API } from './v2/coupons';
import { affiliatesV2API } from './v2/affiliates';
import { mapProductV2ToV1, mapPlanV2ToV1 } from '../../utils/v2ShapeMapper';

// Instância V1 (axios "raw") usada pelos módulos que continuam V1.
const v1Api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
});

v1Api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

v1Api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('token');
      localStorage.removeItem('user_data');
      if (
        window.location.pathname !== '/' &&
        window.location.pathname !== '/login'
      ) {
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  },
);

// Helper: extrai payload da resposta V2 (que vem em { data: { plans, product, ... } })
// e devolve já no shape esperado por { data: ... } simples.
function unwrap(response, key) {
  const data = response.data;
  if (!data) return { data: null };
  if (key && data[key] !== undefined) return { data: data[key] };
  return { data: data.data ?? data };
}

// ============= Produtos =============
export const productsAPI = {
  getAll: async () => {
    const res = await productsV2API.listMine();
    const list = res.data?.products || res.data?.data || [];
    return { data: list.map(mapProductV2ToV1) };
  },
  getById: async (idOrUuid) => {
    const isUuid = typeof idOrUuid === 'string' && idOrUuid.includes('-');
    const res = isUuid
      ? await productsV2API.findByUuid(idOrUuid)
      : await productsV2API.findById(idOrUuid);
    const product = res.data?.product || res.data?.data || res.data;
    return { data: mapProductV2ToV1(product) };
  },
  create: async (data) => {
    const v2Payload = { ...data, image_url: data.image_url ?? data.image };
    delete v2Payload.image;
    const res = await productsV2API.create(v2Payload);
    return { data: mapProductV2ToV1(res.data?.product || res.data?.data || res.data) };
  },
  update: async (id, data) => {
    const v2Payload = { ...data };
    if ('image' in data && !('image_url' in data)) {
      v2Payload.image_url = data.image;
      delete v2Payload.image;
    }
    const res = await productsV2API.update(id, v2Payload);
    return { data: mapProductV2ToV1(res.data?.product || res.data?.data || res.data) };
  },
  delete: (id) => productsV2API.remove(id),
};

// ============= Planos =============
export const plansAPI = {
  getByProduct: async (productId) => {
    const res = await plansV2API.listByProduct(productId);
    const plans = res.data?.plans || res.data?.data || [];
    return { data: plans.map(mapPlanV2ToV1) };
  },
  create: async (productId, data) => {
    const v2Payload = {
      ...data,
      product_id: Number(productId),
      price_cents: data.price_cents ?? data.price,
    };
    delete v2Payload.price;
    const res = await plansV2API.create(v2Payload);
    return { data: mapPlanV2ToV1(res.data?.plan || res.data?.data || res.data) };
  },
  update: async (_productId, planId, data) => {
    const v2Payload = { ...data };
    if ('price' in data && !('price_cents' in data)) {
      v2Payload.price_cents = data.price;
      delete v2Payload.price;
    }
    const res = await plansV2API.update(planId, v2Payload);
    return { data: mapPlanV2ToV1(res.data?.plan || res.data?.data || res.data) };
  },
  delete: (_productId, planId) => plansV2API.remove(planId),
};

// ============= Checkouts =============
export const checkoutsAPI = {
  getByProduct: async (productId) => {
    const res = await checkoutsV2API.listByProduct(productId);
    return unwrap(res, 'checkouts');
  },
  create: async (productId, data) => {
    const res = await checkoutsV2API.create({ ...data, product_id: Number(productId) });
    return unwrap(res, 'checkout');
  },
  update: async (_productId, checkoutId, data) => {
    const res = await checkoutsV2API.update(checkoutId, data);
    return unwrap(res, 'checkout');
  },
  delete: (_productId, checkoutId) => checkoutsV2API.remove(checkoutId),
};

// ============= Cupons =============
export const couponsAPI = {
  getByProduct: async (productId) => {
    const res = await couponsV2API.listByProduct(productId);
    return unwrap(res, 'coupons');
  },
  create: async (productId, data) => {
    const res = await couponsV2API.create({ ...data, product_id: Number(productId) });
    return unwrap(res, 'coupon');
  },
  update: async (_productId, couponId, data) => {
    const res = await couponsV2API.update(couponId, data);
    return unwrap(res, 'coupon');
  },
  delete: (_productId, couponId) => couponsV2API.remove(couponId),
};

// ============= Afiliados (vínculos por produto) =============
export const productAffiliatesAPI = {
  getByProduct: async (productId) => {
    const res = await affiliatesV2API.listByProduct(productId);
    return unwrap(res, 'links');
  },
  create: async (productId, data) => {
    const res = await affiliatesV2API.create({
      product_id: Number(productId),
      user_id: data.user_id,
      plan_id: data.plan_id ?? null,
      commission_value_override: data.commission_value_override ?? data.commission,
    });
    return unwrap(res, 'link');
  },
  update: async (_productId, affiliateId, data) => {
    const res = await affiliatesV2API.updateCommissionOverride(affiliateId, {
      commission_value_override:
        data.commission_value_override ?? data.commission,
    });
    return unwrap(res, 'link');
  },
  delete: (_productId, affiliateId) => affiliatesV2API.remove(affiliateId),
};

// ============= Mantidos V1 (regras inalteradas) =============

export const productUrlsAPI = {
  getByProduct: (productId) => v1Api.get(`/products/${productId}/urls`),
  create: (productId, data) => v1Api.post(`/products/${productId}/urls`, data),
  update: (productId, urlId, data) => v1Api.put(`/products/${productId}/urls/${urlId}`, data),
  delete: (productId, urlId) => v1Api.delete(`/products/${productId}/urls/${urlId}`),
};

export const affiliateCommissionsAPI = {
  getByProduct: (productId) => v1Api.get(`/products/${productId}/affiliate-commissions`),
  create: (productId, data) => v1Api.post(`/products/${productId}/affiliate-commissions`, data),
  update: (productId, commissionId, data) =>
    v1Api.put(`/products/${productId}/affiliate-commissions/${commissionId}`, data),
  delete: (productId, commissionId) =>
    v1Api.delete(`/products/${productId}/affiliate-commissions/${commissionId}`),
};

export const affiliateGoalsAPI = {
  getByProduct: (productId) => v1Api.get(`/products/${productId}/affiliate-goals`),
  create: (productId, data) => v1Api.post(`/products/${productId}/affiliate-goals`, data),
  update: (productId, goalId, data) => v1Api.put(`/products/${productId}/affiliate-goals/${goalId}`, data),
  delete: (productId, goalId) => v1Api.delete(`/products/${productId}/affiliate-goals/${goalId}`),
};

export const productFilesAPI = {
  getByProduct: (productId) => v1Api.get(`/products/${productId}/files`),
  create: (productId, data) => v1Api.post(`/products/${productId}/files`, data),
  update: (productId, fileId, data) => v1Api.put(`/products/${productId}/files/${fileId}`, data),
  delete: (productId, fileId) => v1Api.delete(`/products/${productId}/files/${fileId}`),
};

export const productComponentsAPI = {
  getByProduct: (productId) => v1Api.get(`/products/${productId}/components`),
  create: (productId, data) => v1Api.post(`/products/${productId}/components`, data),
  update: (productId, componentId, data) =>
    v1Api.put(`/products/${productId}/components/${componentId}`, data),
  delete: (productId, componentId) => v1Api.delete(`/products/${productId}/components/${componentId}`),
};

export const upsellsAPI = {
  getByProduct: (productId) => v1Api.get(`/products/${productId}/upsells`),
  create: (productId, data) => v1Api.post(`/products/${productId}/upsells`, data),
  update: (productId, upsellId, data) => v1Api.put(`/products/${productId}/upsells/${upsellId}`, data),
  delete: (productId, upsellId) => v1Api.delete(`/products/${productId}/upsells/${upsellId}`),
};

// Compatibilidade: getProducts -> productsAPI.getAll (V2 listMine + shape map)
const ProductsService = {
  getProducts: async () => {
    try {
      const { data } = await productsAPI.getAll();
      return { data: data || [], success: true };
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
      throw error;
    }
  },
};

export default ProductsService;
